/**
 * Deterministic, generated avatars (APP_SPEC §13: "avatar may include";
 * docs/APP_PLAN.md §3's approved non-goal "avatar image upload" — avatars
 * are generated client-side from the user id, never uploaded). Pure and
 * deterministic on purpose so the same user always renders the same
 * avatar everywhere, with no network request and nothing to moderate.
 */
import { semanticColors } from '@perplexicon/design-tokens';

/** The fixed palette an avatar's background color is chosen from, keyed by a hash of its seed. */
const AVATAR_PALETTE: readonly string[] = [
  semanticColors.frame,
  semanticColors.frameElevated,
  semanticColors.success,
  semanticColors.danger,
  semanticColors.info,
  semanticColors.wood,
  semanticColors.link,
  semanticColors.accentHover,
];

/** A small, deterministic (FNV-1a-style) string hash — stable across runs, browsers, and platforms. */
export function hashSeed(seed: string): number {
  let hash = 2166136261;
  for (let i = 0; i < seed.length; i += 1) {
    hash ^= seed.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

/** Deterministically maps a seed (e.g. a user id) to one color in `AVATAR_PALETTE`. */
export function colorForSeed(seed: string): string {
  const index = hashSeed(seed) % AVATAR_PALETTE.length;
  return AVATAR_PALETTE[index] ?? (AVATAR_PALETTE[0] as string);
}

/** Up to two initials rendered inside the avatar, derived from a display label (typically a username). */
export function initialsFor(label: string): string {
  const trimmed = label.trim();
  if (trimmed.length === 0) return '?';
  return trimmed.slice(0, 2).toUpperCase();
}
